/**
 * Perimeter detection deep-scan catalog (2026-05-14).
 *
 * Extends `detectionSensorCatalog.ts` (Optex initial seed) with the full
 * verified Optex perimeter lineup plus Takex (Japan, photoelectric beam
 * specialist) and GJD (UK, external PIR + LED illuminator specialist).
 *
 * Every entry's specs come from a primary manufacturer URL — datasheet
 * page or product page — cited above each entry. Where a single field
 * (HFOV, mounting height) was absent from the page the entry inherits
 * the conservative default for the device class; see the inline note.
 *
 * IDs avoid colliding with `OPTEX_DETECTION_SENSORS` IDs already shipping
 * in detectionSensorCatalog.ts (vxs-am, vxs-dam, hx-40am, qxi-st, vxi-st,
 * wxi-st, fx-360, cx-702, bxs-st, bxs-am, flipx-standard, ftn-st, ax-70tn,
 * ax-130tn, ax-200tn, sl-100tnr, sl-200tnr).
 *
 * Pattern conventions match detectionSensorCatalog.ts:
 *   - 'cone'     wall PIR (HX, VX, VXI, WX, WXI, RXC, BX/BXS curtain pair)
 *   - 'full'     ceiling 360° (FX-360, SX-360Z, CX-702 wide)
 *   - 'curtain'  narrow projection (FlipX narrow, FTN, BX-80, CX-702 long)
 *   - 'boundary' twin-curtain wall device (BX Shield)
 *   - 'beam'     point-to-point pair (AX, SL, TXF, PB-IN, PR, TAKEX MS-12)
 *   - 'fence'    along-fence (placeholder — no fence-tension product here)
 *
 * LiDAR (REDSCAN) renders as 'full' with `ceilingDiameterByHeightM` keyed
 * to the rectangular footprint diagonal, since the existing renderer only
 * supports radial coverage. Phase-4 work would add a proper rectangular
 * LiDAR pattern.
 */

import type { DetectionSensorCatalogEntry } from './detectionSensorCatalog';

export const PERIMETER_DEEP: DetectionSensorCatalogEntry[] = [
  // ════════════════════════════════════════════════════════════════════════
  // OPTEX — outdoor high-mount wall PIR (HX series)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.optex-europe.com/products/intrusion-detection/hx-40
  {
    id: 'optex-hx-40',
    manufacturer: 'Optex',
    model: 'HX-40',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 35,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 85° high-mount outdoor PIR, animal tolerance',
  },

  // https://www.optex-europe.com/products/intrusion-detection/hx-40dam
  {
    id: 'optex-hx-40dam',
    manufacturer: 'Optex',
    model: 'HX-40DAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 50,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 85° dual-tech high-mount, anti-mask',
  },

  // https://www.optex-europe.com/products/intrusion-detection/hx-80n
  {
    id: 'optex-hx-80n',
    manufacturer: 'Optex',
    model: 'HX-80N',
    pattern: 'curtain',
    category: 'outdoor-wall-pir',
    rangeM: 24,
    // Datasheet: 24 m × 2 m narrow corridor → ~5° at 24 m.
    hfovDeg: 5,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 35,
    outputType: 'NC_NO_selectable',
    tagline: '24 m × 2 m long-narrow corridor PIR, 20 zones',
  },

  // https://www.optex-europe.com/products/intrusion-detection/hx-80nam
  {
    id: 'optex-hx-80nam',
    manufacturer: 'Optex',
    model: 'HX-80NAM',
    pattern: 'curtain',
    category: 'outdoor-wall-pir',
    rangeM: 24,
    hfovDeg: 5,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 40,
    outputType: 'NC_NO_selectable',
    tagline: '24 m × 2 m long-narrow with active IR anti-mask',
  },

  // https://www.optexamerica.com/products/intrusion-detection/hx-80nram
  {
    id: 'optex-hx-80nram',
    manufacturer: 'Optex',
    model: 'HX-80NRAM',
    pattern: 'curtain',
    category: 'outdoor-wall-pir',
    rangeM: 24,
    hfovDeg: 5,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    outputType: 'NC_NO_selectable',
    tagline: '24 m × 2 m wireless battery PIR with anti-mask',
  },

  // ════════════════════════════════════════════════════════════════════════
  // OPTEX — VX Infinity (VXI) wall PIR — 90° wide
  // ════════════════════════════════════════════════════════════════════════

  // https://www.optex-europe.com/products/intrusion-detection/vxi-am
  {
    id: 'optex-vxi-am',
    manufacturer: 'Optex',
    model: 'VXI-AM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 5.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 24,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 90° outdoor PIR with active IR anti-mask',
  },

  // https://www.optex-europe.com/products/intrusion-detection/vxi-dam
  {
    id: 'optex-vxi-dam',
    manufacturer: 'Optex',
    model: 'VXI-DAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 5.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 45,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 35,
    outputType: 'NC_NO_selectable',
    tagline: '12 m dual-tech PIR + microwave with anti-mask',
  },

  // https://www.optex-europe.com/products/intrusion-detection/vxi-r
  {
    id: 'optex-vxi-r',
    manufacturer: 'Optex',
    model: 'VXI-R',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 5.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 90° wireless battery outdoor PIR',
  },

  // ════════════════════════════════════════════════════════════════════════
  // OPTEX — WX Infinity / WX Shield — 180° panoramic
  // ════════════════════════════════════════════════════════════════════════

  // https://www.optex-europe.com/products/intrusion-detection/wxs-am
  {
    id: 'optex-wxs-am',
    manufacturer: 'Optex',
    model: 'WXS-AM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 9],
    hfovDeg: 180,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 2.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 23,
    outputType: 'NC_NO_selectable',
    tagline: '180° panoramic outdoor PIR, low 12 m / high 9 m',
  },

  // https://www.optex-europe.com/products/intrusion-detection/wxs-dam
  {
    id: 'optex-wxs-dam',
    manufacturer: 'Optex',
    model: 'WXS-DAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 9],
    hfovDeg: 180,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 2.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 45,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 24,
    outputType: 'NC_NO_selectable',
    tagline: '180° panoramic dual-tech 4 PIR + 2 MW, anti-mask',
  },

  // https://www.optex-europe.com/products/intrusion-detection/wxi-am
  {
    id: 'optex-wxi-am',
    manufacturer: 'Optex',
    model: 'WXI-AM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 180,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 23,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 180° PIR with full-lens anti-mask',
  },

  // ════════════════════════════════════════════════════════════════════════
  // OPTEX — BX Shield / BX boundary curtain (twin curtain wall device)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.optex-europe.com/products/intrusion-detection/bxs-st-w
  {
    id: 'optex-bxs-st-w',
    manufacturer: 'Optex',
    model: 'BXS-ST(W)',
    pattern: 'boundary',
    category: 'boundary-pir',
    rangeM: 12,
    rangeStepsM: [12, 8, 5, 2],
    hfovDeg: 180,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 31,
    outputType: 'NC_NO_selectable',
    tagline: 'Left+right 12 m boundary curtains (24 m total)',
  },

  // https://www.optex-europe.com/products/intrusion-detection/bx-80n
  {
    id: 'optex-bx-80n',
    manufacturer: 'Optex',
    model: 'BX-80N',
    pattern: 'boundary',
    category: 'boundary-pir',
    rangeM: 12,
    rangeStepsM: [12, 8, 5, 2],
    hfovDeg: 180,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 24,
    outputType: 'NC_NO_selectable',
    tagline: 'Outdoor curtain PIR 2-12 m each side (legacy, replaced by BX Shield)',
  },

  // ════════════════════════════════════════════════════════════════════════
  // OPTEX — FTN compact narrow curtain (5×1 m windows/balcony)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.optex-europe.com/products/intrusion-detection/ftn-r
  {
    id: 'optex-ftn-r',
    manufacturer: 'Optex',
    model: 'FTN-R',
    pattern: 'curtain',
    category: 'curtain-pir',
    rangeM: 5,
    rangeStepsM: [5, 2],
    hfovDeg: 12,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 2.5,
    powerVMax: 10,
    currentMaTypical: 3,
    outputType: 'NC_NO_selectable',
    tagline: 'Wireless 5 m × 1 m compact narrow PIR (windows/balconies)',
  },

  // https://www.optex-europe.com/products/intrusion-detection/ftn-ram
  {
    id: 'optex-ftn-ram',
    manufacturer: 'Optex',
    model: 'FTN-RAM',
    pattern: 'curtain',
    category: 'curtain-pir',
    rangeM: 5,
    rangeStepsM: [5, 2],
    hfovDeg: 12,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 2.5,
    powerVMax: 10,
    currentMaTypical: 3,
    outputType: 'NC_NO_selectable',
    tagline: 'Wireless 5 m × 1 m narrow PIR with anti-mask',
  },

  // ════════════════════════════════════════════════════════════════════════
  // OPTEX — indoor PIR
  // ════════════════════════════════════════════════════════════════════════

  // https://www.optex-europe.com/products/intrusion-detection/rxc-st
  {
    id: 'optex-rxc-st',
    manufacturer: 'Optex',
    model: 'RXC-ST',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.0,
    mountingHeightMinM: 1.5,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    powerVMin: 9.5,
    powerVMax: 16,
    currentMaTypical: 8,
    outputType: 'NC_NO_selectable',
    tagline: 'Indoor 12 m × 12 m × 85° wall PIR',
  },

  // https://www.optex-europe.com/products/intrusion-detection/cx-702-mkii
  {
    id: 'optex-cx-702-mkii',
    manufacturer: 'Optex',
    model: 'CX-702 MKII',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 21,
    rangeStepsM: [21, 10],
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 1.5,
    mountingHeightMaxM: 3.6,
    mounting: ['wall'],
    detectionMethod: 'pir',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    powerVMin: 9.5,
    powerVMax: 16,
    currentMaTypical: 11,
    outputType: 'NC_NO_selectable',
    tagline: 'Indoor 21 m × 21 m wide or 45 m corridor lens',
  },

  // https://www.optex-europe.com/products/intrusion-detection/sx-360z
  {
    id: 'optex-sx-360z',
    manufacturer: 'Optex',
    model: 'SX-360Z',
    pattern: 'full',
    category: 'indoor-ceiling-pir',
    rangeM: 9,
    hfovDeg: 360,
    ceilingDiameterByHeightM: {
      2.4: 12,
      3.0: 14,
      4.0: 16,
      5.0: 18,
    },
    mountingHeightDefaultM: 3.0,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 5.0,
    mounting: ['ceiling'],
    detectionMethod: 'pir',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    outputType: 'NC_NO_selectable',
    tagline: 'Indoor 360° ceiling PIR, up to 18 m diameter @ 5 m',
  },

  // https://www.optex-europe.com/products/intrusion-detection/flx-s-st
  // (existing seed has 'optex-flipx-standard'; this is the FLX-S-ST variant
  //  with narrow-mode 18 m × 5° detection added)
  {
    id: 'optex-flx-s-st',
    manufacturer: 'Optex',
    model: 'FLX-S-ST',
    pattern: 'curtain',
    category: 'curtain-pir',
    rangeM: 18,
    rangeStepsM: [18, 12],
    hfovDeg: 5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    powerVMin: 9.5,
    powerVMax: 16,
    currentMaTypical: 8,
    outputType: 'NC_NO_selectable',
    tagline: 'Indoor FlipX 18 m × 5° narrow (flip 180° for 12 m × 85° wide)',
  },

  // ════════════════════════════════════════════════════════════════════════
  // OPTEX — Active IR beam barriers (AX hardwired)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.optex-europe.com/products/intrusion-detection/ax-100tf
  {
    id: 'optex-ax-100tf',
    manufacturer: 'Optex',
    model: 'AX-100TF',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 30,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 28,
    currentMaTypical: 38,
    outputType: 'NC_relay',
    tagline: '30 m four-channel quad-beam barrier',
  },

  // https://www.optex-europe.com/products/intrusion-detection/ax-200tf
  {
    id: 'optex-ax-200tf',
    manufacturer: 'Optex',
    model: 'AX-200TF',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 60,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 28,
    currentMaTypical: 45,
    outputType: 'NC_relay',
    tagline: '60 m four-channel stackable beam barrier',
  },

  // ════════════════════════════════════════════════════════════════════════
  // OPTEX — Active IR beam barriers (AX wireless/battery)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.optex-europe.com/products/intrusion-detection/ax-100tfr
  {
    id: 'optex-ax-100tfr',
    manufacturer: 'Optex',
    model: 'AX-100TFR',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 30,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    outputType: 'NC_relay',
    tagline: '30 m wireless 4-channel beam (SAFT LSH20 lithium)',
  },

  // https://www.optex-europe.com/products/intrusion-detection/ax-200tfr
  {
    id: 'optex-ax-200tfr',
    manufacturer: 'Optex',
    model: 'AX-200TFR',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 60,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    outputType: 'NC_relay',
    tagline: '60 m wireless 4-channel beam (SAFT LSH20 lithium)',
  },

  // ════════════════════════════════════════════════════════════════════════
  // OPTEX — SL Smart Line beam (single-channel QN, quad QDM/QDP)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.optex-europe.com/products/intrusion-detection/sl-200qn
  {
    id: 'optex-sl-200qn',
    manufacturer: 'Optex',
    model: 'SL-200QN',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 60,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 30,
    outputType: 'NC_relay',
    tagline: '60 m single-channel IR beam, aspherical lenses',
  },

  // https://www.optex-europe.com/products/intrusion-detection/sl-350qn
  {
    id: 'optex-sl-350qn',
    manufacturer: 'Optex',
    model: 'SL-350QN',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 100,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 30,
    outputType: 'NC_relay',
    tagline: '100 m single-channel IR beam',
  },

  // https://www.optex-europe.com/products/intrusion-detection/sl-200qdm
  {
    id: 'optex-sl-200qdm',
    manufacturer: 'Optex',
    model: 'SL-200QDM',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 60,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 30,
    outputType: 'NC_relay',
    tagline: '60 m four-channel quad-beam, dual modulation',
  },

  // https://www.optex-europe.com/products/intrusion-detection/sl-350qdm
  {
    id: 'optex-sl-350qdm',
    manufacturer: 'Optex',
    model: 'SL-350QDM',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 100,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 30,
    outputType: 'NC_relay',
    tagline: '100 m four-channel quad-beam barrier',
  },

  // https://www.optex-europe.com/products/intrusion-detection/sl-350qdp
  {
    id: 'optex-sl-350qdp',
    manufacturer: 'Optex',
    model: 'SL-350QDP',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 100,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 30,
    outputType: 'NC_relay',
    tagline: '100 m professional 4-channel beam tower-ready',
  },

  // https://www.optex-europe.com/products/intrusion-detection/sl-650qdp
  {
    id: 'optex-sl-650qdp',
    manufacturer: 'Optex',
    model: 'SL-650QDP',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 200,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 30,
    outputType: 'NC_relay',
    tagline: '200 m long-range 4-channel professional beam',
  },

  // ════════════════════════════════════════════════════════════════════════
  // OPTEX — REDSCAN LiDAR (mapped to 'full' 360 with diagonal as diameter)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.optex-europe.com/products/intrusion-detection/rls-2020s
  {
    id: 'optex-rls-2020s',
    manufacturer: 'Optex',
    model: 'REDSCAN RLS-2020S',
    pattern: 'full',
    category: 'indoor-ceiling-pir',
    rangeM: 20,
    hfovDeg: 95,
    // 20 × 20 m at 95° — diagonal ≈ 28 m used for ceiling-style overlay.
    ceilingDiameterByHeightM: {
      2.0: 28,
      4.0: 28,
    },
    mountingHeightDefaultM: 4.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole', 'ceiling'],
    detectionMethod: 'laser',
    ipRating: 'IP66',
    operatingTempMinC: -40,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 30,
    currentMaTypical: 500,
    outputType: 'solid_state',
    tagline: 'LiDAR 20 m × 20 m × 95°, Grade 3 (PoE)',
  },

  // https://www.optex-europe.com/products/intrusion-detection/rls-2020a-redscan-mini-pro
  {
    id: 'optex-rls-2020a',
    manufacturer: 'Optex',
    model: 'REDSCAN mini-Pro RLS-2020A',
    pattern: 'full',
    category: 'indoor-ceiling-pir',
    rangeM: 20,
    hfovDeg: 95,
    ceilingDiameterByHeightM: {
      2.0: 28,
      4.0: 28,
    },
    mountingHeightDefaultM: 4.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole', 'ceiling'],
    detectionMethod: 'laser',
    ipRating: 'IP66',
    operatingTempMinC: -40,
    operatingTempMaxC: 60,
    powerVMin: 19.2,
    powerVMax: 30,
    currentMaTypical: 420,
    outputType: 'solid_state',
    tagline: 'LiDAR mini-Pro 20 m × 20 m × 95° (PoE+)',
  },

  // https://www.optex-europe.com/products/intrusion-detection/rls-3060v
  {
    id: 'optex-rls-3060v',
    manufacturer: 'Optex',
    model: 'REDSCAN Pro RLS-3060V',
    pattern: 'full',
    category: 'indoor-ceiling-pir',
    rangeM: 60,
    hfovDeg: 190,
    // 30 × 60 m at 190° — long axis (60 m) used as effective diameter.
    ceilingDiameterByHeightM: {
      2.0: 60,
      4.0: 60,
    },
    mountingHeightDefaultM: 4.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'laser',
    ipRating: 'IP66',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 19.2,
    powerVMax: 30,
    currentMaTypical: 500,
    outputType: 'solid_state',
    tagline: 'Pro LiDAR 30 m × 60 m × 190° with assist camera',
  },

  // https://www.optex-europe.com/products/intrusion-detection/rls-50100v
  {
    id: 'optex-rls-50100v',
    manufacturer: 'Optex',
    model: 'REDSCAN Pro RLS-50100V',
    pattern: 'full',
    category: 'indoor-ceiling-pir',
    rangeM: 100,
    hfovDeg: 190,
    ceilingDiameterByHeightM: {
      2.0: 100,
      4.0: 100,
    },
    mountingHeightDefaultM: 4.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'laser',
    ipRating: 'IP66',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 19.2,
    powerVMax: 30,
    currentMaTypical: 500,
    outputType: 'solid_state',
    tagline: 'Long-range LiDAR 50 m × 100 m × 190° with camera',
  },

  // ════════════════════════════════════════════════════════════════════════
  // TAKEX — outdoor photoelectric beam (quad PB-IN, battery TXF)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.takex-eng.co.jp/en/products/item/26/
  {
    id: 'takex-pb-in-100hfa',
    manufacturer: 'Takex',
    model: 'PB-IN-100HFA',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 100,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP55',
    operatingTempMinC: -35,
    operatingTempMaxC: 66,
    powerVMin: 12,
    powerVMax: 30,
    currentMaTypical: 70,
    outputType: 'NC_NO_selectable',
    tagline: '100 m quad-beam AND-gated outdoor photoelectric',
  },

  // https://www.takex-eng.co.jp/en/products/item/28/
  {
    id: 'takex-pb-in-100at',
    manufacturer: 'Takex',
    model: 'PB-IN-100AT',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 100,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    operatingTempMinC: -35,
    operatingTempMaxC: 66,
    powerVMin: 12,
    powerVMax: 30,
    currentMaTypical: 57,
    outputType: 'NC_NO_selectable',
    tagline: '100 m quad-beam (TR/RE 4 simultaneous interruption)',
  },

  // https://www.takex-eng.co.jp/en/products/item/119/
  {
    id: 'takex-txf-125e',
    manufacturer: 'Takex',
    model: 'TXF-125E',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 100,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    batteryOperated: true,
    ipRating: 'IP65',
    operatingTempMinC: -25,
    operatingTempMaxC: 60,
    powerVMin: 3.6,
    powerVMax: 3.9,
    outputType: 'NC_NO_selectable',
    tagline: '100 m battery quad-beam, SAFT LS33600 (5-yr life)',
  },

  // https://www.takex-eng.co.jp/en/products/item/150/
  {
    id: 'takex-txf-125dm',
    manufacturer: 'Takex',
    model: 'TXF-125DM',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 100,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    batteryOperated: true,
    ipRating: 'IP65',
    operatingTempMinC: -25,
    operatingTempMaxC: 60,
    powerVMin: 3.6,
    powerVMax: 3.9,
    outputType: 'NC_NO_selectable',
    tagline: '100 m battery quad-beam (3-yr life, mesh-ready)',
  },

  // ════════════════════════════════════════════════════════════════════════
  // TAKEX — outdoor reflective beam (single-sided wiring)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.takex-eng.co.jp/en/products/item/145/
  {
    id: 'takex-pr-30be',
    manufacturer: 'Takex',
    model: 'PR-30BE',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 30,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    operatingTempMinC: -25,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 30,
    outputType: 'NC_NO_selectable',
    tagline: '30 m ToF reflective beam, single-sided wiring',
  },

  // https://www.takex-eng.co.jp/en/products/item/66/
  {
    id: 'takex-pr-11be',
    manufacturer: 'Takex',
    model: 'PR-11BE',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 11,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    operatingTempMinC: -25,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 30,
    outputType: 'NC_NO_selectable',
    tagline: '11 m outdoor / 15 m indoor reflective IR beam',
  },

  // ════════════════════════════════════════════════════════════════════════
  // TAKEX — outdoor PIR (MS-12FE dual zone, PIR-T high-mount triple-mirror)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.takex-eng.co.jp/en/products/item/8/
  {
    id: 'takex-ms-12fe',
    manufacturer: 'Takex',
    model: 'MS-12FE',
    pattern: 'boundary',
    category: 'boundary-pir',
    rangeM: 12,
    hfovDeg: 180,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 2.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    powerVMin: 12,
    powerVMax: 30,
    currentMaTypical: 40,
    outputType: 'NC_NO_selectable',
    tagline: '2-12 m dual-zone outdoor PIR, ±90° area-mask sheet',
  },

  // https://www.takex-eng.co.jp/en/products/item/18/
  {
    id: 'takex-mw-50',
    manufacturer: 'Takex',
    model: 'MW-50',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 50,
    hfovDeg: 20,
    mountingHeightDefaultM: 2.0,
    mountingHeightMinM: 1.0,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 12,
    powerVMax: 30,
    outputType: 'NC_NO_selectable',
    tagline: '1-50 m microwave perimeter sensor, ±90° aim',
  },

  // https://www.takex-eng.co.jp/en/products/item/118/
  {
    id: 'takex-pir-t15we',
    manufacturer: 'Takex',
    model: 'PIR-T15WE',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 80,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 6.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -25,
    operatingTempMaxC: 55,
    powerVMin: 9,
    powerVMax: 28,
    currentMaTypical: 40,
    outputType: 'NC_NO_selectable',
    tagline: '15 m × 80° triple-mirror, 4-mode high-mount PIR',
  },

  // https://www.takex-eng.co.jp/en/products/item/122/
  {
    id: 'takex-pir-t15am',
    manufacturer: 'Takex',
    model: 'PIR-T15AM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 80,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 6.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -25,
    operatingTempMaxC: 55,
    powerVMin: 9,
    powerVMax: 28,
    currentMaTypical: 40,
    outputType: 'NC_NO_selectable',
    tagline: '15 m × 80° triple-mirror PIR with active anti-mask',
  },

  // https://www.takex-eng.co.jp/en/products/item/124/
  {
    id: 'takex-pir-t40nam',
    manufacturer: 'Takex',
    model: 'PIR-T40NAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 45,
    hfovDeg: 60,
    mountingHeightDefaultM: 3.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 6.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -25,
    operatingTempMaxC: 55,
    powerVMin: 9,
    powerVMax: 28,
    outputType: 'NC_NO_selectable',
    tagline: '45 m triple-mirror long-range PIR, 23-pair, anti-mask',
  },

  // ════════════════════════════════════════════════════════════════════════
  // GJD — external PIR (D-TECT series)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.gjd.co.uk/products/d-tect-2-gjd300/
  {
    id: 'gjd-d-tect-2',
    manufacturer: 'GJD',
    model: 'D-TECT 2 (GJD300)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 30,
    hfovDeg: 12,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP65',
    operatingTempMinC: -25,
    operatingTempMaxC: 50,
    powerVMin: 12,
    powerVMax: 24,
    outputType: 'NC_NO_selectable',
    tagline: '30 m quad PIR with programmable beam range',
  },

  // https://www.gjd.co.uk/products/d-tect-3-gjd310/
  {
    id: 'gjd-d-tect-3',
    manufacturer: 'GJD',
    model: 'D-TECT 3 (GJD310)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 30,
    hfovDeg: 12,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    ipRating: 'IP65',
    operatingTempMinC: -25,
    operatingTempMaxC: 50,
    powerVMin: 12,
    powerVMax: 24,
    outputType: 'NC_NO_selectable',
    tagline: 'BS8418 quad PIR + 10.587 GHz microwave dual-tech 30 m',
  },

  // https://www.gjd.co.uk/products/d-tect-3-ip-gjd260/
  {
    id: 'gjd-d-tect-3-ip',
    manufacturer: 'GJD',
    model: 'D-TECT 3 IP (GJD260)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 30,
    hfovDeg: 12,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    ipRating: 'IP65',
    operatingTempMinC: -25,
    operatingTempMaxC: 50,
    powerVMin: 12,
    powerVMax: 48,
    outputType: 'solid_state',
    tagline: 'PoE IP-connected dual-tech 30 m, native VMS integration',
  },

  // https://www.gjd.co.uk/products/d-tect-dual-am-gjd369/
  {
    id: 'gjd-d-tect-dual-am',
    manufacturer: 'GJD',
    model: 'D-TECT Dual AM (GJD369)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 30,
    hfovDeg: 12,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    ipRating: 'IP65',
    operatingTempMinC: -25,
    operatingTempMaxC: 50,
    powerVMin: 12,
    powerVMax: 24,
    outputType: 'NC_NO_selectable',
    tagline: '30 m BS8418 dual-tech with anti-mask sensing module',
  },

  // https://www.gjd.co.uk/products/d-tect-60-gjd320-external-motion-detector/
  {
    id: 'gjd-d-tect-60',
    manufacturer: 'GJD',
    model: 'D-TECT 60 (GJD320)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 60,
    rangeStepsM: [60, 50, 40],
    hfovDeg: 10,
    mountingHeightDefaultM: 3.0,
    mountingHeightMinM: 2.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP65',
    operatingTempMinC: -25,
    operatingTempMaxC: 50,
    powerVMin: 12,
    powerVMax: 24,
    outputType: 'NC_NO_selectable',
    tagline: '40-60 m dual PIR long-range, BS8418 alarm trigger',
  },

  // https://www.gjd.co.uk/products/d-tect-1-ht-gjd112/ (specs from product line)
  {
    id: 'gjd-d-tect-1-ht',
    manufacturer: 'GJD',
    model: 'D-TECT 1 HT (GJD112)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 30,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP65',
    operatingTempMinC: -25,
    operatingTempMaxC: 50,
    powerVMin: 12,
    powerVMax: 24,
    outputType: 'NC_NO_selectable',
    tagline: 'Cost-effective external PIR, pre-alarm / camera trigger',
  },

  // ════════════════════════════════════════════════════════════════════════
  // GJD — D-TECT Laser (LiDAR)
  // ════════════════════════════════════════════════════════════════════════

  // https://www.gjd.co.uk/products/d-tect-laser-gjd500/
  {
    id: 'gjd-d-tect-laser-25',
    manufacturer: 'GJD',
    model: 'D-TECT Laser (GJD500)',
    pattern: 'full',
    category: 'indoor-ceiling-pir',
    rangeM: 25,
    hfovDeg: 95,
    ceilingDiameterByHeightM: {
      2.0: 35,
      4.0: 35,
    },
    mountingHeightDefaultM: 4.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'laser',
    ipRating: 'IP65',
    operatingTempMinC: -25,
    operatingTempMaxC: 50,
    powerVMin: 12,
    powerVMax: 24,
    outputType: 'solid_state',
    tagline: '25 m × 25 m laser scanner, 10 cm zone resolution',
  },

  // https://www.gjd.co.uk/wp-content/uploads/2021/09/GJD505-D-TECT-Laser-5m-x-5m-Manual.pdf
  {
    id: 'gjd-d-tect-laser-5',
    manufacturer: 'GJD',
    model: 'D-TECT Laser (GJD505)',
    pattern: 'full',
    category: 'indoor-ceiling-pir',
    rangeM: 5,
    hfovDeg: 95,
    ceilingDiameterByHeightM: {
      2.0: 7,
      4.0: 7,
    },
    mountingHeightDefaultM: 3.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'laser',
    ipRating: 'IP65',
    operatingTempMinC: -25,
    operatingTempMaxC: 50,
    powerVMin: 12,
    powerVMax: 24,
    outputType: 'solid_state',
    tagline: '5 m × 5 m short-range laser scanner, asset protection',
  },

  // ════════════════════════════════════════════════════════════════════════
  // GJD — Clarius LED illuminators (rendered as 'cone' for area coverage)
  //   Note: illuminators are NOT detection sensors but are catalogued here
  //   for perimeter design completeness — the renderer shows the IR/WL beam
  //   footprint as if it were a wide PIR cone, with detectionMethod = 'pir'
  //   sentinel (renderer has no 'illuminator' type yet). Future work: add
  //   'illuminator' detectionMethod + a distinct visual treatment.
  // ════════════════════════════════════════════════════════════════════════

  // https://www.gjd.co.uk/products/medium-clarius-plus-infra-red-illuminator-im-8/
  {
    id: 'gjd-clarius-plus-im-8',
    manufacturer: 'GJD',
    model: 'Clarius PLUS IM-8 (850 nm)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 187,
    rangeStepsM: [187, 100, 80, 50],
    hfovDeg: 30,
    mountingHeightDefaultM: 3.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 8.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP66',
    operatingTempMinC: -50,
    operatingTempMaxC: 60,
    powerVMin: 12,
    powerVMax: 32,
    outputType: 'solid_state',
    tagline: 'IR 850 nm 187 m medium illuminator, 10°/30°/60°/95° lens',
  },

  // https://www.gjd.co.uk/products/large-clarius-plus-infra-red-illuminator-il-8/
  {
    id: 'gjd-clarius-plus-il-8',
    manufacturer: 'GJD',
    model: 'Clarius PLUS IL-8 (850 nm)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 228,
    rangeStepsM: [228, 150, 100, 60],
    hfovDeg: 30,
    mountingHeightDefaultM: 4.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 10.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP66',
    operatingTempMinC: -50,
    operatingTempMaxC: 60,
    powerVMin: 12,
    powerVMax: 32,
    outputType: 'solid_state',
    tagline: 'IR 850 nm 228 m large illuminator, interchangeable lens',
  },

  // Clarius IX (extra-large), specs from Clarius datasheet
  {
    id: 'gjd-clarius-plus-ix-8',
    manufacturer: 'GJD',
    model: 'Clarius PLUS IX-8 (850 nm)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 280,
    rangeStepsM: [280, 200, 150, 100],
    hfovDeg: 30,
    mountingHeightDefaultM: 4.0,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 10.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP66',
    operatingTempMinC: -50,
    operatingTempMaxC: 60,
    powerVMin: 12,
    powerVMax: 32,
    outputType: 'solid_state',
    tagline: 'IR 850 nm 280 m extra-large illuminator, 52 W',
  },
];
